import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

const HIGHLIGHTS = [
  ["Founder", "The Federation Companies — established 2019"],
  ["$1.1B", "Affordable housing pipeline supported at Gilbane Development"],
  ["Georgetown", "M.S., Real Estate Finance"],
  ["East Carolina", "B.S., Construction Management — College of Engineering"],
  ["Board Member", "Preservation North Carolina (previously Columbus Building Company)"],
  ["Full life cycle", "Sourcing · structured finance · construction · asset management"],
];

const fade = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

export default function AjBio() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="bg-[#F7F5F0] text-[#1C2220] min-h-screen" data-testid="aj-bio-page">
      {/* Minimal header */}
      <header className="sticky top-0 z-50 bg-[#F7F5F0]/85 backdrop-blur-xl border-b border-[#1C2220]/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" data-testid="aj-logo-home">
            <img src="/federation-logo.png" alt="The Federation Companies" className="h-9 md:h-10 w-auto" />
          </Link>
          <Link
            to="/"
            data-testid="aj-bio-back"
            className="inline-flex items-center gap-2 text-sm text-[#1C2220]/70 hover:text-[#C56A4E] transition-colors"
          >
            <ArrowLeft size={16} /> Back to site
          </Link>
        </div>
      </header>

      <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-16 md:py-24">
        <motion.p
          variants={fade} initial="hidden" animate="visible"
          className="text-xs uppercase tracking-[0.3em] text-[#C56A4E] mb-6"
        >
          Executive Team · Leadership
        </motion.p>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Portrait */}
          <div className="lg:col-span-5">
            <motion.div
              variants={fade} initial="hidden" animate="visible"
              className="lg:sticky lg:top-28"
            >
              <div className="overflow-hidden rounded-sm aspect-[4/5] bg-[#EEEAE0]">
                <img
                  src="/team/aj-casavant.jpg"
                  alt="AJ Casavant"
                  className="h-full w-full object-cover object-top"
                  data-testid="aj-bio-photo"
                />
              </div>
              <h1 data-testid="aj-bio-name" className="font-heading text-4xl md:text-5xl tracking-tight mt-8">
                AJ Casavant
              </h1>
              <p className="mt-2 text-xs uppercase tracking-[0.2em] text-[#C56A4E]">
                Managing Principal
              </p>
            </motion.div>
          </div>

          {/* Bio */}
          <div className="lg:col-span-6 lg:col-start-7">
            <motion.div variants={fade} custom={1} initial="hidden" animate="visible" className="space-y-6 text-lg leading-relaxed text-[#1C2220]/80">
              <p className="font-heading text-2xl md:text-3xl leading-snug text-[#1C2220]">
                AJ Casavant, a North Carolina native, has been a leading figure in
                affordable housing development throughout the Southeast.
              </p>
              <p>
                His work with top firms such as Gilbane Development and the Related
                Companies has involved complex acquisitions and projects leveraging
                public-private financing tools like low-income housing tax credits,
                tax-exempt bonds, and project-based rental assistance — making quality,
                affordable housing a reality for these communities. That experience spans
                the full life cycle of affordable housing deals, from sourcing and
                structured finance through construction and long-term asset management.
              </p>
              <p>
                In 2019, AJ founded The Federation Companies, a real estate development
                firm dedicated to preserving and creating affordable housing. Federation
                embodies AJ's commitment to building sustainable, community-centered
                developments. Before launching Federation, AJ played an integral role at
                Gilbane Development, supporting a <strong className="text-[#1C2220]">$1.1 billion</strong>{" "}
                development pipeline of affordable housing.
              </p>
              <p>
                He currently serves as a board member of Preservation North Carolina, and
                previously served on the board of the Columbus Building Company.
              </p>
              <p>
                AJ holds a Master's degree in Real Estate Finance from Georgetown University
                and a Bachelor of Science in Construction Management from East Carolina
                University's College of Engineering.
              </p>
            </motion.div>

            {/* Highlights */}
            <motion.div variants={fade} custom={2} initial="hidden" animate="visible" className="mt-12 border-t border-[#1C2220]/15">
              {HIGHLIGHTS.map(([k, v]) => (
                <div key={k} className="grid sm:grid-cols-12 gap-2 sm:gap-6 py-5 border-b border-[#1C2220]/15">
                  <div className="sm:col-span-4 font-heading text-xl text-[#1C2220]">{k}</div>
                  <div className="sm:col-span-8 text-sm text-[#1C2220]/70">{v}</div>
                </div>
              ))}
            </motion.div>

            <motion.div variants={fade} custom={3} initial="hidden" animate="visible" className="mt-10">
              <Link
                to="/#contact"
                data-testid="aj-bio-contact"
                className="inline-flex items-center gap-3 rounded-sm bg-[#1C2220] text-white text-sm px-8 py-4 hover:bg-[#2A332F] hover:-translate-y-0.5 transition-[transform,background-color] duration-300 group"
              >
                Get in touch
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}
